import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import {
    LayoutDashboard,
    Target,
    FileText,
    Smartphone,
    Settings,
    Key,
    Bell,
    Search,
    ChevronDown,
    LogOut,
    User,
    ChevronLeft,
    Sparkles,
    Plug,
    ListChecks,
    History,
    Layers,
    Calendar,
    Tv
} from 'lucide-react';
import VerificationBanner from '../components/VerificationBanner';
import './MainLayout.css';
import { config } from '../config/env';
import { useFeatureFlags } from '../context/FeatureFlagContext';

function MainLayout({ children, headerLeft }) {
    const { user, logout } = useAuth();
    const { currentWorkspace, workspaces, switchWorkspace } = useWorkspace();
    const { isFeatureEnabled } = useFeatureFlags();
    const navigate = useNavigate();

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);

    const handleSwitchWorkspace = (workspace) => {
        switchWorkspace(workspace);
        setShowWorkspaceMenu(false);
        navigate('/'); // Redirect to dashboard on workspace switch
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/test-goals', icon: Target, label: 'New Goal' },
        { path: '/goals', icon: ListChecks, label: 'Test Goals' },
        { path: '/execution-plans', icon: Layers, label: 'Execution Plans' },
        { path: '/schedules', icon: Calendar, label: 'Schedules' },
        { path: '/executions', icon: History, label: 'Executions' },
        // { path: '/test-cases', icon: FileText, label: 'Test Library' },
        { path: '/apps', icon: Smartphone, label: 'Apps' },
        isFeatureEnabled('enable-live-sessions') && { path: '/interactive-sessions', icon: Tv, label: 'Live Sessions' },
        { path: '/integrations', icon: Plug, label: 'Integrations' },
        { path: '/settings', icon: Settings, label: 'Settings' },

        // { path: '/api-keys', icon: Key, label: 'API Keys' },
    ].filter(Boolean);

    return (
        <div className={`main-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div className="logo">
                        {sidebarCollapsed ? (
                            <img
                                src={`${process.env.PUBLIC_URL}/rova-icon-white.png`}
                                alt="ROVA"
                                className="logo-icon-img"
                                style={{ width: '24px', height: '24px' }}
                            />
                        ) : (
                            <img
                                src={`${process.env.PUBLIC_URL}/rova-ai-logo-wordmark-white.png`}
                                alt="ROVA AI"
                                className="logo-wordmark"
                                style={{ height: '24px', width: 'auto' }}
                            />
                        )}
                    </div>
                    <button
                        className="sidebar-toggle"
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    >
                        <ChevronLeft size={16} />
                    </button>
                </div>

                {/* Project Selector */}
                {!sidebarCollapsed && (
                    <div className="sidebar-project-selector">
                        <div
                            className={`project-selector ${showWorkspaceMenu ? 'active' : ''}`}
                            onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
                        >
                            <div className="project-icon">
                                <Smartphone size={14} />
                            </div>
                            <div className="project-details">
                                {/* <span className="project-label">Workspace</span> */}
                                <span className="project-name">
                                    {currentWorkspace?.name || 'Select Workspace'}
                                </span>
                            </div>
                            <ChevronDown size={14} className={`selector-chevron ${showWorkspaceMenu ? 'open' : ''}`} />
                        </div>

                        {showWorkspaceMenu && (
                            <div className="workspace-menu">
                                <div className="workspace-menu-header">
                                    <span>My Workspaces</span>
                                </div>
                                <div className="workspace-list">
                                    {workspaces.map(ws => (
                                        <button
                                            key={ws.id}
                                            className={`workspace-item ${currentWorkspace?.id === ws.id ? 'active' : ''}`}
                                            onClick={() => handleSwitchWorkspace(ws)}
                                        >
                                            <div className="workspace-icon">
                                                {ws.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="workspace-info">
                                                <span className="workspace-name">{ws.name}</span>
                                                <span className="workspace-role">
                                                    {ws.WorkspaceMember?.role || 'Member'}
                                                </span>
                                            </div>
                                            {currentWorkspace?.id === ws.id && <div className="active-indicator"></div>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <nav className="sidebar-nav">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                                `nav-item ${isActive ? 'active' : ''}`
                            }
                            data-tooltip={sidebarCollapsed ? item.label : undefined}
                        >
                            <item.icon size={20} />
                            {!sidebarCollapsed && <span>{item.label}</span>}
                        </NavLink>
                    ))}
                </nav>

                {/* User Section */}
                <div className="sidebar-footer">
                    <div
                        className="user-section"
                        onClick={() => setShowUserMenu(!showUserMenu)}
                    >
                        <div className="user-avatar">
                            {user?.fullName?.[0] || user?.firstName?.[0] || user?.email?.[0] || 'U'}
                        </div>
                        {!sidebarCollapsed && (
                            <>
                                <div className="user-info">
                                    <span className="user-name">
                                        {user?.fullName || user?.firstName || user?.email?.split('@')[0]}
                                    </span>
                                    <span className="user-role">
                                        {currentWorkspace?.name || 'Workspace'}
                                    </span>
                                </div>
                                <ChevronDown size={14} className={`user-chevron ${showUserMenu ? 'open' : ''}`} />
                            </>
                        )}
                    </div>

                    {showUserMenu && (
                        <div className="user-menu">
                            <Link to={`${config.API_URL}/api/auth/oauth/profile`} className="user-menu-item" target='_blank'>
                                <Settings size={16} />
                                <span>Settings</span>
                            </Link>
                            <button className="user-menu-item" onClick={handleLogout}>
                                <LogOut size={16} />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <div className="main-content">
                {/* Top Header */}
                <header className={`top-header ${headerLeft ? 'has-left-content' : ''}`}>
                    {headerLeft && (
                        <div className="header-left">
                            {headerLeft}
                        </div>
                    )}
                    <div className="header-right">
                        {/* <div className="search-box">
                            <Search size={16} />
                            <input
                                type="text"
                                placeholder="Search tests, builds..."
                                className="search-input"
                            />
                        </div> */}
                        {/* <button className="icon-btn notification-btn">
                            <Bell size={18} />
                            <span className="notification-dot"></span>
                        </button> */}
                    </div>
                </header>

                {/* Verification Banner */}
                {user && (!user.isEmailVerified || !user.emailVerified) && (
                    <VerificationBanner user={user} />
                )}

                {/* Page Content */}
                <main className="page-content">
                    {children}
                </main>
            </div>
        </div>
    );
}

export default MainLayout;
